import classNames from 'classnames';
import { arrayOf, bool, number, shape, string, func } from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  BookingTimeInfo, Footer,
  IconSpinner, LayoutSideNavigation, LayoutWrapperFooter, LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar, NamedLink,
  Page,
  PaginationLinks,
  AvatarLarge,
  Avatar
} from '../../components';
import {formatDate} from '../../util/dates';
import config from '../../config';
import { TopbarContainer } from '..';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { ensureCurrentUser } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
    txIsEnquired
} from '../../util/transaction';
import { DATE_TYPE_DATE, propTypes } from '../../util/types';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';

import css from './ThreadsPage.css';
import { loadData, loadThreadPage } from './ThreadsPage.duck';

import Arrow from '@material-ui/icons/ArrowForwardIos';
import Pagination from '../../components/PaginationSteps/PaginationSteps';

const MAX_MOBILE_SCREEN_WIDTH = 600;

// Translated name of the state of the given transaction
export const txState = (intl, tx) => {
  if (txIsEnquired(tx)){
    return {
        nameClassName: css.nameNotEmphasized,
        bookingClassName: css.bookingNoActionNeeded,
        lastTransitionedAtClassName: css.lastTransitionedAtNotEmphasized,
        stateClassName: css.stateNoActionNeeded,
        state: intl.formatMessage({
          id: 'InboxPage.stateDelivered',
        }),
      };
  }else{
    console.warn('This transition is unknown:', tx.attributes.lastTransition);
    return null;
  }
};

// Functional component as internal helper to print BookingTimeInfo if that is needed
const BookingInfoMaybe = props => {
  const { bookingClassName, isOrder, intl, tx, unitType } = props;
  return (
    <div className={classNames(css.bookingInfoWrapper, bookingClassName)}>
      <BookingTimeInfo
        bookingClassName={bookingClassName}
        isOrder={isOrder}
        intl={intl}
        tx={tx}
        unitType={unitType}
        dateType={DATE_TYPE_DATE}
      />
    </div>
  );
};

BookingInfoMaybe.propTypes = {
  intl: intlShape.isRequired,
  isOrder: bool.isRequired,
  tx: propTypes.transaction.isRequired,
  unitType: propTypes.bookingUnitType.isRequired,
};

export const ThreadItem = props => {
  const { thread, currentUser } = props;
  const { other_user_name, other_user_image } = thread;
  const initials = other_user_name?.split(' ').map(name => name[0]).join('');

  // Determine if the thread is unread using last_checked
  const isUnread = () => {
    const lastChecked = thread.last_checked;
    return new Date(thread.thread_update_at) > new Date(lastChecked);
  };

  const threadClass = isUnread() ? css.unreadMessageContainer : css.orderContainer;

  // Calculate the time since the message was sent
  const timeSinceMessage = () => {
    const messageDate = new Date(thread.thread_update_at);
    const now = new Date();
    const differenceInHours = (now - messageDate) / (1000 * 60 * 60);

    if (differenceInHours < 24) {
      return formatDistanceToNow(messageDate, { addSuffix: true, locale: es });
    } else if (differenceInHours < 48) {
      return 'ayer';
    } else {
      return format(messageDate, 'dd/MM/yyyy', { locale: es });
    }
  };

  return (
    <NamedLink name="MessagePage" params={{threadId: thread.id}} className={css.threadLink}>
      <div className={threadClass}>
        <div className={css.orderDetailRow}>
          {other_user_image ? (
            <img src={other_user_image} alt={other_user_name} className={css.avatarMobile} />
          ) : (
            <div className={css.avatarMobile}>{initials}</div>
          )}
          <div className={css.orderDetailFlex}>
            <div className={css.orderDetailListing}>
              <p className={css.orderDetail}>{other_user_name}</p>
              <p className={css.orderDetail}>{thread.last_message}</p>
            </div>
            <div className={css.orderDetailTimeStamps}>
              {timeSinceMessage()}
            </div>
          </div>
          <div className={css.orderDetailListingImage}>
            <img src={thread.listing_image} alt="Listing" />
          </div>
          <div className={css.orderDetailArrow}><Arrow /></div>
        </div>
      </div>
    </NamedLink>
  );
};

ThreadItem.propTypes = {
  thread: shape({
    id: string.isRequired,
    last_message: string.isRequired,
    other_user_name: string.isRequired,
    other_user_image: string.isRequired,
    listing_image: string.isRequired,
    updated_at: string.isRequired,
    last_checked: string.isRequired,
  }).isRequired,
  currentUser: propTypes.currentUser.isRequired,
};

export const ThreadsPageComponent = props => {
  const {
    fetchInProgress,
    fetchOrdersOrSalesError,
    intl,
    threads,
    loadThreadPage,
    currentUser,
    currentPage,
    totalPages,
    totalCount,
    scrollingDisabled,
    fetchThreadsError,
  } = props;

  const title = intl.formatMessage({ id: 'ThreadsPage.title' });

  const toThreadItem = thread => (
    <li key={thread.id} className={css.listItem}>
      <ThreadItem thread={thread} currentUser={currentUser} />
    </li>
  );

  const noThreads =
    !fetchInProgress && threads.length === 0 ? (
      <li key="noThreads" className={css.noResults}>
        <FormattedMessage id={'ThreadsPage.noThreadsFound'} />
      </li>
    ) : null;

  const handlePageChange = (newPage) => {
    loadThreadPage(newPage);
  };

  return (
    <Page title={title} className={css.root} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            className={css.topbar}
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
            currentPage="ThreadsPage"
          />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav />
        <LayoutWrapperMain>
          <div className={css.headerContainer}>
            <h1 className={css.messagesHeader}>Mensajes</h1>
          </div>
          <div className={css.content}>
            {fetchThreadsError ? (
              <p className={css.error}>
                <FormattedMessage id="ThreadsPage.fetchFailed" />
              </p>
            ) : fetchInProgress ? (
              <div className={css.spinnerContainer}>
                <IconSpinner />
              </div>
            ) : (
              <ul className={css.itemList}>
                {threads.map(toThreadItem)}
                {noThreads}
              </ul>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalCount={totalPages}
                onPageChange={handlePageChange}
                siblingCount={1}
                className={css.pagination}
              />
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

ThreadsPageComponent.defaultProps = {
  unitType: config.bookingUnitType,
  currentUser: null,
  currentUserHasOrders: null,
  fetchOrdersOrSalesError: null,
  pagination: null,
  providerNotificationCount: 0,
  sendVerificationEmailError: null,
};

ThreadsPageComponent.propTypes = {
  unitType: propTypes.bookingUnitType,
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchOrdersOrSalesError: propTypes.error,
  pagination: propTypes.pagination,
  providerNotificationCount: number,
  scrollingDisabled: bool.isRequired,
  threads: arrayOf(
    shape({
      id: string.isRequired,
      last_message: string.isRequired,
      other_user_name: string.isRequired,
      other_user_image: string.isRequired,
      listing_image: string.isRequired,
    })
  ).isRequired,
  fetchThreadsError: shape({}),
  intl: intlShape.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: number,
  totalPages: number,
  totalCount: number,
};

const mapStateToProps = state => {
  const { threads, fetchInProgress, fetchThreadsError, currentPage, totalPages, totalEntries } = state.ThreadsPage;
  const { currentUser, currentUserNotificationCount: providerNotificationCount } = state.user;

  return {
    currentUser,
    fetchInProgress,
    fetchThreadsError,
    currentPage,
    totalPages,
    totalCount: totalEntries,
    providerNotificationCount,
    scrollingDisabled: isScrollingDisabled(state),
    threads,
  };
};

const mapDispatchToProps = {
  loadThreadPage,
};

const ThreadsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ThreadsPageComponent);

ThreadsPage.loadData = loadData;

export default ThreadsPage;
